/**
 * ModalERP — componente base de modais do ERP.
 * Overlay com blur, header com ícone, body scrollável e footer de ações.
 */

import { useEffect, useRef } from 'react'
import { X, Loader2 } from 'lucide-react'
import './ModalERP.css'

const SIZE_CLASS = {
  sm: 'modal-erp-sm',
  md: 'modal-erp-md',
  lg: 'modal-erp-lg',
  xl: 'modal-erp-xl',
  full: 'modal-erp-full'
}

const VARIANT_CLASS = {
  default: 'modal-erp-variant-default',
  danger: 'modal-erp-variant-danger',
  success: 'modal-erp-variant-success'
}

function ModalERP({
  isOpen,
  onClose,
  title,
  subtitle,
  icon,
  children,
  size = 'md',
  variant = 'default',
  footer,
  loading = false,
  showCloseButton = true,
  closeOnOverlayClick = true,
  preventCloseWhileLoading = true,
  layer = 0
}) {
  const contentRef = useRef(null)

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key !== 'Escape' || !isOpen) return
      if (preventCloseWhileLoading && loading) return
      onClose()
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, onClose, loading, preventCloseWhileLoading])

  useEffect(() => {
    if (!isOpen || !contentRef.current) return
    const focusable = contentRef.current.querySelector(
      'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])'
    )
    focusable?.focus()
  }, [isOpen])

  if (!isOpen) return null

  const handleOverlayClick = () => {
    if (!closeOnOverlayClick) return
    if (preventCloseWhileLoading && loading) return
    onClose()
  }

  const handleClose = () => {
    if (preventCloseWhileLoading && loading) return
    onClose()
  }

  return (
    <div
      className="modal-erp-overlay"
      style={{ zIndex: 10000 + layer * 20 }}
      onClick={handleOverlayClick}
      role="presentation"
    >
      <div
        ref={contentRef}
        className={`modal-erp-content ${SIZE_CLASS[size] || SIZE_CLASS.md} ${VARIANT_CLASS[variant] || VARIANT_CLASS.default}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'modal-erp-title' : undefined}
      >
        {(title || showCloseButton) && (
          <div className="modal-erp-header">
            <div className="modal-erp-header-content">
              {icon && <div className="modal-erp-icon">{icon}</div>}
              <div className="modal-erp-header-text">
                {title && (
                  <h2 id="modal-erp-title" className="modal-erp-title">
                    {title}
                  </h2>
                )}
                {subtitle && <p className="modal-erp-subtitle">{subtitle}</p>}
              </div>
            </div>
            {showCloseButton && (
              <button
                type="button"
                className="modal-erp-close"
                onClick={handleClose}
                aria-label="Fechar modal"
                disabled={preventCloseWhileLoading && loading}
              >
                <X size={20} />
              </button>
            )}
          </div>
        )}

        <div className="modal-erp-body">{children}</div>

        {footer && <div className="modal-erp-footer">{footer}</div>}

        {loading && (
          <div className="modal-erp-loading-overlay" aria-hidden="true">
            <Loader2 className="modal-erp-spinner" size={32} />
          </div>
        )}
      </div>
    </div>
  )
}

export default ModalERP
